import { useCallback, useEffect, useRef, useState } from 'react'
import {
    BulkInviteProgress,
    getActiveBulkInvitations,
    getBulkInvitationProgress,
} from '../services/bulkInvitationService'

/* The server dictates cadence via `pollAfterMs`; these are only client-side guards. Mirrors
   BulkCompleteAction's polling (UBQ-4542) so the two bulk operations behave the same way. */
const POLL_FLOOR_MS = 2000
const POLL_CEIL_MS = 30000
const POLL_HIDDEN_MS = 30000 // slow right down while the tab is hidden
const POLL_JITTER = 0.15 // ±15% so concurrent viewers don't poll in lockstep
const MAX_POLL_FAILURES = 5 // consecutive, so one gateway hiccup doesn't freeze the bar

const nextPollDelay = (recommendedMs: number): number => {
    const base = Math.min(
        POLL_CEIL_MS,
        Math.max(POLL_FLOOR_MS, recommendedMs || POLL_FLOOR_MS),
    )
    const jitter = base * POLL_JITTER * (Math.random() * 2 - 1)
    return Math.round(base + jitter)
}

interface Options {
    orgId: string
    /** Called once when a batch reaches a terminal state. Refresh the grid and release selection here. */
    onFinished?: (progress: BulkInviteProgress) => void
    /**
     * Pick up a batch already running when the component mounts, so navigating away and back — or
     * reloading — does not lose it.
     */
    reattach?: boolean
}

interface Tracker {
    progress: BulkInviteProgress | null
    isRunning: boolean
    /** Start following a batch that was just started. */
    track: (batchId: string, startedAt: number) => void
    /** Drop the bar, after the user dismisses it or it has been shown long enough. */
    reset: () => void
}

/**
 * UBQ-5486: follow a bulk invitation batch to completion.
 *
 * Needed because `POST /users/inviteUser` returns as soon as the batch is queued — the worker then paces
 * through it at `invite.bulk.send-interval-ms` per user, so thirty users take about ten seconds. Treating
 * that response as "done" refreshed the grid before the users existed, which is exactly the reported bug:
 * one user of thirty in the listing.
 *
 * Shared by the Users screen rather than reimplemented per caller: the jitter, hidden-tab backoff and
 * consecutive-failure cap are easy to get subtly wrong twice.
 */
export const useBulkInvitationProgress = ({
    orgId,
    onFinished,
    reattach = false,
}: Options): Tracker => {
    const [progress, setProgress] = useState<BulkInviteProgress | null>(null)

    const timerRef = useRef<number | null>(null)
    const failuresRef = useRef(0)
    /* Indirection so a scheduled timer always calls the latest closure rather than a stale one, and so
       changing the callback does not restart polling mid-batch. */
    const runPollRef = useRef<(batchId: string, startedAt: number) => void>()
    const onFinishedRef = useRef(onFinished)
    onFinishedRef.current = onFinished

    const clearTimer = useCallback(() => {
        if (timerRef.current !== null) {
            window.clearTimeout(timerRef.current)
            timerRef.current = null
        }
    }, [])

    const pollProgress = useCallback(
        async (batchId: string, startedAt: number) => {
            try {
                const next = await getBulkInvitationProgress(
                    batchId,
                    orgId,
                    startedAt,
                )
                failuresRef.current = 0
                setProgress(next)

                if (next.status === 'RUNNING') {
                    const delay = document.hidden
                        ? POLL_HIDDEN_MS
                        : nextPollDelay(next.pollAfterMs)
                    timerRef.current = window.setTimeout(
                        () => runPollRef.current?.(batchId, startedAt),
                        delay,
                    )
                    return
                }
                onFinishedRef.current?.(next)
            } catch {
                /* A single transient hiccup must not freeze the bar. Keep polling and only give up after
                   several consecutive failures. */
                failuresRef.current += 1
                if (failuresRef.current >= MAX_POLL_FAILURES) {
                    clearTimer()
                    return
                }
                timerRef.current = window.setTimeout(
                    () => runPollRef.current?.(batchId, startedAt),
                    nextPollDelay(0),
                )
            }
        },
        [orgId, clearTimer],
    )

    runPollRef.current = pollProgress

    const track = useCallback(
        (batchId: string, startedAt: number) => {
            failuresRef.current = 0
            clearTimer()
            /* Show the bar immediately at zero rather than waiting for the first poll a couple of seconds
               away — otherwise the dialog closes and nothing appears to have happened. */
            setProgress({
                batchId,
                startedAt,
                total: 0,
                sent: 0,
                failed: 0,
                pending: 0,
                status: 'RUNNING',
                pollAfterMs: POLL_FLOOR_MS,
            })
            runPollRef.current?.(batchId, startedAt)
        },
        [clearTimer],
    )

    const reset = useCallback(() => {
        clearTimer()
        setProgress(null)
    }, [clearTimer])

    useEffect(() => {
        if (!reattach) {
            return clearTimer
        }
        let cancelled = false
        const attach = async () => {
            try {
                const active = await getActiveBulkInvitations(orgId)
                if (cancelled || active.length === 0) return
                const batch = active[0]
                setProgress(batch)
                runPollRef.current?.(batch.batchId, batch.startedAt)
            } catch {
                // Nothing to re-attach to is the normal case; stay quiet.
            }
        }
        attach()
        return () => {
            cancelled = true
            clearTimer()
        }
    }, [orgId, reattach, clearTimer])

    // Force a poll when the user comes back to the tab, so the bar isn't stale on focus.
    useEffect(() => {
        const onVisible = () => {
            if (!document.hidden && progress?.status === 'RUNNING') {
                clearTimer()
                runPollRef.current?.(progress.batchId, progress.startedAt)
            }
        }
        document.addEventListener('visibilitychange', onVisible)
        return () => document.removeEventListener('visibilitychange', onVisible)
    }, [progress, clearTimer])

    return {
        progress,
        isRunning: progress?.status === 'RUNNING',
        track,
        reset,
    }
}
